import React, { useEffect, useState } from "react";
import {
    Container,
    Typography,
    Box,
    Paper,
    FormControlLabel,
    Switch,
    Button,
} from "@mui/material";
import {
    AirplanemodeInactive,
    SatelliteAlt,
    Announcement,
    Public,
} from "@mui/icons-material";
import ListaVuelosAeropuerto from "./listaVuelosAeropuerto";

const airports = [
    {
        id: "SUMU",
        name: "Carrasco",
        fullName: "Aeropuerto Internacional Gral. Cesáreo L. Berisso",
        code: "SUMU",
    },
    {
        id: "SULS",
        name: "Laguna del Sauce",
        fullName: "Aeropuerto Internacional C/C Carlos A. Curbelo",
        code: "SULS",
    },
    {
        id: "SUSO",
        name: "Salto",
        fullName: "Aeropuerto Internacional de Nueva Hespérides",
        code: "SUSO",
    },
    {
        id: "SUAA",
        name: "Melilla",
        fullName: "Aeropuerto Internacional Ángel S. Adami",
        code: "SUAA",
    },
];

export default function PortalAutoServicioAero() {
    const [expanded, setExpanded] = useState("SUMU");
    const [flightsByAirport, setFlightsByAirport] = useState({});
    const [useLocalTime, setUseLocalTime] = useState(false);

    const handleListaVuelosChangeState = (id, isExpanded) => {
        setExpanded(isExpanded ? id : -1);
    };

    const handleDownloadPDF = (flight) => {
        if (!flight?.pdfUrl) {
            return;
        }
        const link = document.createElement("a");
        link.href = flight.pdfUrl;
        link.download = flight.fileName ?? "carta de vuelo.pdf";
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    const formatFlightDateTime = (
        year,
        month,
        day,
        hour,
        minute,
        toLocalTime
    ) => {
        const hasInvalidValue = [year, month, day, hour, minute].some(
            (value) => value === null || value === undefined || Number.isNaN(value)
        );
        if (hasInvalidValue) {
            return { dateLabel: "", timeLabel: "" };
        }

        const baseDate = new Date(
            Date.UTC(year, month - 1, day, hour, minute)
        );
        const adjustedDate = toLocalTime
            ? new Date(baseDate.getTime() - 3 * 60 * 60 * 1000)
            : baseDate;

        const dateLabel = `${String(adjustedDate.getUTCDate()).padStart(
            2,
            "0"
        )}/${String(adjustedDate.getUTCMonth() + 1).padStart(2, "0")}/${String(
            adjustedDate.getUTCFullYear()
        )}`;
        const timeLabel = `${String(adjustedDate.getUTCHours()).padStart(
            2,
            "0"
        )}:${String(adjustedDate.getUTCMinutes()).padStart(2, "0")}`;
        return { dateLabel, timeLabel };
    };

    useEffect(() => {
        const loadFlights = async () => {
            try {
                const [flightsResponse, airportsResponse] = await Promise.all([
                    fetch(
                        `${process.env.PUBLIC_URL}/data/files.csv`
                    ),
                    fetch(
                        `${process.env.PUBLIC_URL}/data/aeropuertos_icao.json`
                    ),
                ]);

                if (!flightsResponse.ok) {
                    throw new Error("No se pudo cargar datos_planes_vuelos");
                }
                const airportsData = airportsResponse.ok
                    ? await airportsResponse.json()
                    : [];
                const airportsByIcao = airportsData.reduce((acc, airport) => {
                    if (airport?.idOaci) {
                        acc[airport.idOaci] = airport;
                    }
                    return acc;
                }, {});

                const text = await flightsResponse.text();
                const flights = text
                    .split("\n")
                    .map((line) => line.trim())
                    .filter(Boolean)
                    .filter((line) => !line.startsWith("filename,"))
                    .map((line) => {
                        const [rawFileName, created] = line.split(",");
                        return {
                            fileName: rawFileName?.replace(/^"|"$/g, ""),
                            created: created?.replace(/^"|"$/g, ""),
                        };
                    })
                    .filter((entry) => entry?.fileName)
                    .map((entry) => {
                        const baseName = entry.fileName.replace(".pdf", "");
                        const parts = baseName.split("_");
                        if (parts.length < 5) {
                            return null;
                        }
                        const datePart = parts[1];
                        const origin = parts[2];
                        const destino = parts[3];
                        const flightId = parts[4];
                        if (!datePart || datePart.length < 6) {
                            return null;
                        }
                        const createdDate = entry.created?.split(" ")[0] ?? "";
                        const [year, month] = createdDate
                            ? createdDate.split("-").map(Number)
                            : [];
                        const day = datePart.slice(0, 2);
                        const hour = datePart.slice(2, 4);
                        const minute = datePart.slice(4, 6);
                        const destinoInfo = airportsByIcao[destino];
                        const destinoLabel = destinoInfo
                            ? `${destinoInfo.nombre} - ${destinoInfo.ciudad}`
                            : destino;
                        return {
                            id: flightId,
                            origin,
                            destino: destinoLabel,
                            utcYear: year,
                            utcMonth: month,
                            utcDay: Number(day),
                            utcHour: Number(hour),
                            utcMinute: Number(minute),
                            pdfUrl: `${process.env.PUBLIC_URL}/data/${entry.fileName}`,
                            fileName: entry.fileName,
                        };
                    })
                    .filter(Boolean);

                const nowUtcMinus30 = new Date(
                    Date.now() - 30 * 60 * 1000
                ).getTime();
                const getFlightTime = (flight) =>
                    Date.UTC(
                        flight.utcYear,
                        (flight.utcMonth ?? 1) - 1,
                        flight.utcDay,
                        flight.utcHour,
                        flight.utcMinute
                    );
                const upcomingFlights = flights
                    .filter((flight) => {
                        const flightTime = getFlightTime(flight);
                        if (Number.isNaN(flightTime)) {
                            return false;
                        }
                        return flightTime >= nowUtcMinus30;
                    })
                    .sort((a, b) => getFlightTime(a) - getFlightTime(b));

                const grouped = upcomingFlights.reduce((acc, flight) => {
                    if (!acc[flight.origin]) {
                        acc[flight.origin] = [];
                    }
                    acc[flight.origin].push(flight);
                    return acc;
                }, {});
                const airportIds = Object.keys(grouped);
                setFlightsByAirport(grouped);
                setExpanded((prev) => {
                    if (airportIds.length === 1) {
                        return airportIds[0];
                    }
                    if (prev && grouped[prev]) {
                        return prev;
                    }
                    return airportIds[0] ?? -1;
                });
            } catch (error) {
                console.error(error);
                setFlightsByAirport({});
            }
        };

        loadFlights();
    }, []);

    return (
        <Box sx={{ minHeight: "100vh", bgcolor: "white" }}>
            <Container maxWidth="xl" sx={{ pt: 0, pb: { xs: 10, md: 20 } }}>
                <Paper
                    elevation={0}
                    sx={{ p: 3, mb: 3, bgcolor: "white", borderRadius: 2 }}
                >
                    <Typography
                        variant="h6"
                        gutterBottom
                        sx={{ color: "#3baec7", fontWeight: 600 }}
                    >
                        Documentación de Vuelo
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Seleccione el aeropuerto para visualizar los vuelos
                        disponibles y descargar las cartas meteorológicas
                        correspondientes.
                    </Typography>
                    <Box
                        sx={{
                            mt: 2,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            gap: 1.5,
                        }}
                    >
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={useLocalTime}
                                    onChange={(event) =>
                                        setUseLocalTime(event.target.checked)
                                    }
                                    sx={{
                                        "& .MuiSwitch-switchBase.Mui-checked": {
                                            color: "#3baec7",
                                            "&:hover": { bgcolor: "rgba(59, 174, 199, 0.08)" },
                                        },
                                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                            bgcolor: "#3baec7",
                                        },
                                    }}
                                />
                            }
                            label="Mostrar hora local (UTC-3)"
                        />
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                flexWrap: "wrap",
                            }}
                        >
                        <Button
                            component="a"
                            href="https://www.inumet.gub.uy/tiempo/satelite"
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="outlined"
                            size="small"
                            startIcon={<SatelliteAlt />}
                            sx={{
                                borderColor: "#3baec7",
                                color: "#1a8a9e",
                                "&:hover": { borderColor: "#1a8a9e", bgcolor: "#e0f7fa" }
                            }}
                        >
                            Ver imágenes satelitales
                        </Button>
                        <Button
                            component="a"
                            href="https://www.inumet.gub.uy/aeronautica/productos-aeronauticos#aviso"
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="outlined"
                            size="small"
                            startIcon={<Announcement />}
                            sx={{
                                borderColor: "#3baec7",
                                color: "#1a8a9e",
                                "&:hover": { borderColor: "#1a8a9e", bgcolor: "#e0f7fa" }
                            }}
                        >
                            Avisos de aeródromo
                        </Button>
                        <Button
                            component="a"
                            href="https://www.inumet.gub.uy/aeronautica/productos-aeronauticos#ciz"
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="outlined"
                            size="small"
                            startIcon={<Announcement />}
                            sx={{
                                borderColor: "#3baec7",
                                color: "#1a8a9e",
                                "&:hover": { borderColor: "#1a8a9e", bgcolor: "#e0f7fa" }
                            }}
                        >
                            Avisos por Cizalladura
                        </Button>
                        <Button
                            component="a"
                            href="https://www.inumet.gub.uy/aeronautica/productos-aeronauticos#swxa"
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="outlined"
                            size="small"
                            startIcon={<Public />}
                            sx={{
                                borderColor: "#3baec7",
                                color: "#1a8a9e",
                                "&:hover": { borderColor: "#1a8a9e", bgcolor: "#e0f7fa" }
                            }}
                        >
                            Ver Space Weather Advisories
                        </Button>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            * Esta documentación se considera de carácter oficial y
                            cuenta con el aval de la OMA/SUMU para su plena
                            utilización.
                        </Typography>
                    </Box>
                </Paper>

                {airports.filter(
                    (airport) =>
                        (flightsByAirport[airport.id] ?? []).length > 0
                ).length === 0 ? (
                    <Paper
                        elevation={0}
                        sx={{ p: 3, bgcolor: "white", borderRadius: 2 }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            <AirplanemodeInactive
                                sx={{ fontSize: 40, color: "#90a4ae" }}
                            />
                        <Typography
                            variant="body1"
                            color="text.secondary"
                            align="center"
                        >
                            Sin vuelos disponibles en el momento.
                        </Typography>
                        </Box>
                    </Paper>
                ) : (
                    airports
                        .filter(
                            (airport) =>
                                (flightsByAirport[airport.id] ?? []).length > 0
                        )
                        .map((airport) => (
                            <ListaVuelosAeropuerto
                                key={airport.id}
                                airport={airport}
                                flights={(
                                    flightsByAirport[airport.id] ?? []
                                ).map((flight) => {
                                    const { dateLabel, timeLabel } =
                                        formatFlightDateTime(
                                            flight.utcYear,
                                            flight.utcMonth,
                                            flight.utcDay,
                                            flight.utcHour,
                                            flight.utcMinute,
                                            useLocalTime
                                        );
                                    return {
                                        ...flight,
                                        fechaSalida: dateLabel,
                                        horaSalida: timeLabel,
                                        timeZoneLabel: useLocalTime
                                            ? "Hora Local"
                                            : "UTC",
                                    };
                                })}
                                expandedId={expanded}
                                onToggle={handleListaVuelosChangeState}
                                onDownload={handleDownloadPDF}
                            />
                        ))
                )}

                <Box sx={{ mt: 4, textAlign: "center" }}>
                    <Typography variant="caption" color="text.secondary">
                        © 2025 INUMET - Instituto Uruguayo de Meteorología
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
}
